import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Targeting } from '@feathr/blackbox';
import { AsyncDisplay, Button, EmptyState } from '@feathr/components';
import { cssVar } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import { useSelectedTargetings } from './SelectedTargetings.hooks';
import LocationChip from './SelectedTargetings.LocationChip';
import RemovableTargeting from './SelectedTargetings.RemovableTargeting';
import TargetingsWell from './SelectedTargetings.TargetingsWell';
import { formatTargetingName, getInterestDescription } from './SelectedTargetings.utils';

interface IProps {
  targetings: ListResponse<Targeting>;
  children?: JSX.Element;
  emptyState?: JSX.Element;
  clearButtonText?: string;
}

function SelectedTargetings({
  targetings: targetingsList,
  children,
  emptyState,
  clearButtonText,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    geoTargetings,
    ageDemographicTargetings,
    interestsTargetings,
    allTargetings,
    geoLocationItems,
    isLoadingGeoLocations,
    removeTargeting,
    handleClearAllFilters,
  } = useSelectedTargetings({ targetings: targetingsList });

  const renderSelectedTargetings = (targetings: Targeting[]): JSX.Element[] => {
    return targetings.map((targeting): JSX.Element => {
      const name = formatTargetingName(targeting, t);

      return (
        <RemovableTargeting
          key={targeting.id}
          name={name}
          removeTargeting={removeTargeting}
          targeting={targeting}
        />
      );
    });
  };

  const renderGeoLocations = (): JSX.Element[] => {
    return geoLocationItems.map(
      (item): JSX.Element => (
        <LocationChip
          included={item.included}
          key={`${item.targeting.id}-${item.geoFilterId}`}
          name={item.name}
        />
      ),
    );
  };

  const defaultEmptyState = (
    <EmptyState
      description={t('Get started by selecting some filters')}
      label={t('No filters selected')}
      theme={'neutral'}
    />
  );

  if (allTargetings.length === 0) {
    return <>{emptyState ?? defaultEmptyState}</>;
  }

  return (
    <Flex direction={'column'} gap={cssVar('--spacing-4')}>
      {children}

      <TargetingsWell glue={true} label={t('Locations')} targetings={geoTargetings}>
        <AsyncDisplay isLoading={isLoadingGeoLocations}>
          <Flex gap={cssVar('--spacing-2')} wrap={'wrap'}>
            {renderGeoLocations()}
          </Flex>
        </AsyncDisplay>
      </TargetingsWell>

      <TargetingsWell
        glue={true}
        label={t('Age Demographics')}
        targetings={ageDemographicTargetings}
      >
        {renderSelectedTargetings(ageDemographicTargetings)}
      </TargetingsWell>

      <TargetingsWell
        description={getInterestDescription(interestsTargetings, t)}
        label={t('Interests')}
        targetings={interestsTargetings}
      >
        {renderSelectedTargetings(interestsTargetings)}
      </TargetingsWell>

      {allTargetings.length > 0 && (
        <Button onClick={handleClearAllFilters}>{clearButtonText ?? t('Clear filters')}</Button>
      )}
    </Flex>
  );
}

export default observer(SelectedTargetings);
